import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Prompt {
  id: string;
  question: string;
  description: string;
  placeholder: string;
}

interface SwipeablePromptProps {
  prompts: Prompt[];
  responses: Record<string, string>;
  onResponseChange: (promptId: string, value: string) => void;
  onComplete: () => void;
  accentColor?: string;
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
}

export const SwipeablePrompt: React.FC<SwipeablePromptProps> = ({
  prompts,
  responses,
  onResponseChange,
  onComplete,
  accentColor = 'yellow',
  currentIndex: externalIndex,
  onIndexChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(externalIndex || 0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentPrompt = prompts[currentIndex];
  const progress = ((currentIndex + 1) / prompts.length) * 100;

  useEffect(() => {
    if (externalIndex !== undefined) {
      setCurrentIndex(externalIndex);
    }
  }, [externalIndex]);

  const handleNext = () => {
    if (isTransitioning) return;
    
    if (currentIndex < prompts.length - 1) {
      setIsTransitioning(true);
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      onIndexChange?.(newIndex);
      setTimeout(() => setIsTransitioning(false), 300);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (isTransitioning || currentIndex === 0) return;
    
    setIsTransitioning(true);
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    onIndexChange?.(newIndex);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      handleNext();
    }
    
    if (isRightSwipe) {
      handlePrevious();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  const canContinue = responses[currentPrompt.id]?.trim().length > 0;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress Dots */}
      <div className="flex justify-center items-center space-x-2 mb-6">
        {prompts.map((_, index) => (
          <div
            key={index}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? `w-8 h-2 bg-${accentColor}-500`
                : index < currentIndex
                ? `w-2 h-2 bg-${accentColor}-400`
                : 'w-2 h-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-6">
        <div
          className={`bg-gradient-to-r from-${accentColor}-400 to-${accentColor}-600 h-1.5 rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Swipeable Card Container */}
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="relative overflow-hidden"
      >
        <div
          className={`transform transition-transform duration-300 ease-out ${
            isTransitioning ? 'opacity-50' : 'opacity-100'
          }`}
          style={{
            transform: isTransitioning
              ? currentIndex > 0
                ? 'translateX(-100%)'
                : 'translateX(100%)'
              : 'translateX(0)'
          }}
        >
          {/* Prompt Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
            {/* Question Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                  {currentPrompt.question}
                </h2>
                <span className={`text-${accentColor}-600 font-semibold text-sm whitespace-nowrap ml-3`}>
                  {currentIndex + 1}/{prompts.length}
                </span>
              </div>
              <p className="text-gray-600 text-sm sm:text-base">
                {currentPrompt.description}
              </p>
            </div>

            {/* Text Area */}
            <div className="space-y-3">
              <textarea
                value={responses[currentPrompt.id] || ''}
                onChange={(e) => onResponseChange(currentPrompt.id, e.target.value)}
                placeholder={currentPrompt.placeholder}
                rows={6}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-${accentColor}-500 focus:border-${accentColor}-500 resize-none text-gray-900 placeholder-gray-400 transition-all`}
                style={{ minHeight: '150px' }}
              />
              
              {/* Character Count or Helper Text */}
              <div className="flex justify-between items-center text-sm">
                <p className="text-gray-500">
                  {canContinue ? '✓ Ready to continue' : 'Take your time to reflect...'}
                </p>
                <span className="text-gray-400">
                  {(responses[currentPrompt.id] || '').length} characters
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-6 gap-4">
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className={`flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
            currentIndex === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 active:scale-95'
          }`}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        <button
          onClick={handleNext}
          disabled={!canContinue}
          className={`flex-1 sm:flex-initial flex items-center justify-center px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg ${
            canContinue
              ? `bg-gradient-to-r from-${accentColor}-500 to-${accentColor}-600 text-white hover:shadow-xl active:scale-95`
              : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
          }`}
        >
          {currentIndex === prompts.length - 1 ? (
            <>
              Complete
              <ChevronRight className="w-5 h-5 ml-1" />
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Continue</span>
              <ChevronRight className="w-5 h-5 ml-1" />
            </>
          )}
        </button>
      </div>

      {/* Swipe Hint (Mobile Only) */}
      {currentIndex === 0 && (
        <div className="sm:hidden text-center mt-4 text-sm text-gray-400 animate-pulse">
          👈 Swipe left or right 👉
        </div>
      )}
    </div>
  );
};




